import { z } from "zod"

import { ApiEnrichmentRowKeySchema } from "./enrichments.js"

/**
 * The transfers and recurring surface (PRD: engagement-analysis-layer,
 * phase 6). Single definition of the wire shapes: the API routes import
 * these and the api-client parses responses through them.
 *
 * Group ids are uuid-shaped but deterministic (version nibble 8), written
 * by the pairing stage or derived by the clusterer; z.string().uuid()
 * accepts them alongside ordinary v4 ids.
 */

export const ApiTransferRowSchema = z.object({
  documentId: z.string().uuid(),
  recordType: z.string(),
  recordIndex: z.number().int(),
  recordDate: z.string().nullable(),
  /** Cleaned description when the pipeline has run, else the raw one. */
  description: z.string().nullable(),
  amount: z.string().nullable(),
  currency: z.string().nullable(),
  accountId: z.string().uuid().nullable(),
  /** A re-read moved this row's data under manual work; needs confirming. */
  stale: z.boolean(),
})

export type ApiTransferRow = z.infer<typeof ApiTransferRowSchema>

export const ApiTransferPairSchema = z.object({
  groupId: z.string().uuid(),
  source: z.enum(["auto", "manual"]),
  rows: z.array(ApiTransferRowSchema),
})

export type ApiTransferPair = z.infer<typeof ApiTransferPairSchema>

/**
 * Equal-amount candidates the pairer refused to pair because more than one
 * pairing was equally plausible. Reported for a person to resolve with a
 * manual pair; never paired automatically, by locked decision.
 */
export const ApiAmbiguousTransferClusterSchema = z.object({
  absAmount: z.string(),
  currency: z.string(),
  rows: z.array(ApiTransferRowSchema),
})

export type ApiAmbiguousTransferCluster = z.infer<
  typeof ApiAmbiguousTransferClusterSchema
>

export const ApiTransfersResponseSchema = z.object({
  dateWindowDays: z.number().int(),
  pairs: z.array(ApiTransferPairSchema),
  ambiguousClusters: z.array(ApiAmbiguousTransferClusterSchema),
})

export type ApiTransfersResponse = z.infer<typeof ApiTransfersResponseSchema>

export const ApiAddTransferPairBodySchema = z.object({
  rows: z.tuple([ApiEnrichmentRowKeySchema, ApiEnrichmentRowKeySchema]),
})

export type ApiAddTransferPairBody = z.infer<
  typeof ApiAddTransferPairBodySchema
>

export const ApiAddTransferPairResponseSchema = z.object({
  groupId: z.string().uuid(),
})

export type ApiAddTransferPairResponse = z.infer<
  typeof ApiAddTransferPairResponseSchema
>

export const ApiRemoveTransferPairBodySchema = z.object({
  groupId: z.string().uuid(),
})

export type ApiRemoveTransferPairBody = z.infer<
  typeof ApiRemoveTransferPairBodySchema
>

export const ApiRemoveTransferPairResponseSchema = z.object({
  updatedRows: z.number().int(),
})

export type ApiRemoveTransferPairResponse = z.infer<
  typeof ApiRemoveTransferPairResponseSchema
>

export const transferDateWindowDaysMax = 31

export const ApiTransferSettingsSchema = z.object({
  dateWindowDays: z.number().int().min(0).max(transferDateWindowDaysMax),
})

export type ApiTransferSettings = z.infer<typeof ApiTransferSettingsSchema>

export const ApiRecurringIntervalSchema = z.enum([
  "weekly",
  "biweekly",
  "monthly",
  "quarterly",
  "annual",
  "unknown",
])

export type ApiRecurringInterval = z.infer<typeof ApiRecurringIntervalSchema>

export const ApiRecurringGroupSchema = z.object({
  groupId: z.string().uuid(),
  counterpartyKey: z.string(),
  description: z.string(),
  /** Signed, rounded to the nearest whole currency unit. */
  roundedAmount: z.string(),
  currency: z.string().nullable(),
  interval: ApiRecurringIntervalSchema,
  occurrenceCount: z.number().int(),
  /** Sum of the actual (unrounded) amounts. */
  totalAmount: z.string(),
  firstDate: z.string(),
  lastDate: z.string(),
  rows: z.array(ApiEnrichmentRowKeySchema),
})

export type ApiRecurringGroup = z.infer<typeof ApiRecurringGroupSchema>

export const ApiRecurringResponseSchema = z.object({
  groups: z.array(ApiRecurringGroupSchema),
})

export type ApiRecurringResponse = z.infer<typeof ApiRecurringResponseSchema>
